import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';

import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, forkJoin, map, mergeMap, of, tap } from 'rxjs';

import { BetslipBetBuilderPick } from '../../../../core/picks/betslip-bet-builder-pick';
import { AlertType } from '../../../alerts/models';
import { MinSelectionsBetbuilderError } from '../../../validation/errors/general/min-selections-betbuilder-error';
import { RewardTokensMaxStakeError } from '../../../validation/errors/general/reward-tokens-max-stake-error';
import { RewardTokensMinLegsError } from '../../../validation/errors/general/reward-tokens-min-legs-error';
import { RewardTokensSlipTypeError } from '../../../validation/errors/general/reward-tokens-slip-type-error';
import { StakeNotDividablePreCheckError } from '../../../validation/errors/pre-check/stake-not-dividable-pre-check-error';
import { UnderMinimumStakePreCheckError } from '../../../validation/errors/pre-check/under-minimum-stake-pre-check-error';
import { ErrorViewModel } from '../../../validation/models/models';
import { BetslipErrorMessageFormatterService } from '../../../validation/services/formatting/betslip-error-message-formatter.service';
import { isExtendedMessage, isSimpleMessage } from '../../../validation/services/formatting/message-formatters/message-formatter';
import { selectAllQuickBetErrors, selectQuickBetStakeErrorState } from '../../quick-bet.selectors';
import { QuickBetErrorTrackingService } from '../../services/quick-bet-error-tracking.service';
import { QuickBetUtils } from '../../services/quick-bet-utils.service';
import { QuickBetService } from '../../services/quick-bet.service';

export interface QuickBetPickErrorViewModel {
    errors: Nullable<ErrorViewModel>[];
    showErrors: boolean;
}

@Component({
    selector: 'bs-quick-bet-pick-error',
    templateUrl: './quick-bet-pick-error.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./quick-bet-pick-error.component.scss'],
    standalone: false,
})
export class QuickBetPickErrorComponent implements OnInit {
    @Input() pick: BetslipBetBuilderPick;
    viewModel$: Observable<QuickBetPickErrorViewModel>;

    alertType = AlertType.Error;

    constructor(
        private errorMessageFormatter: BetslipErrorMessageFormatterService,
        private utils: QuickBetUtils,
        private store: Store,
        private quickBetService: QuickBetService,
        private trackingService: QuickBetErrorTrackingService,
    ) {}

    ngOnInit(): void {
        const errors$ = this.getErrors$();
        const hideErrorMessage$ = this.store.select(selectQuickBetStakeErrorState).pipe(
            map(({ stakeState, errors }) => {
                return this.utils.hideError(errors, stakeState.stake);
            }),
        );

        this.viewModel$ = combineLatest([errors$, hideErrorMessage$]).pipe(
            map(([errors, hideErrors]) => ({
                errors,
                showErrors: !hideErrors,
            })),
            tap((pickError) => {
                this.trackingService.pickError = pickError;
            }),
        );
    }

    getErrors$() {
        return this.store.select(selectAllQuickBetErrors).pipe(
            mergeMap((errors) => {
                let betslipErrors = errors.filter(
                    (e) =>
                        !(e instanceof RewardTokensMaxStakeError) &&
                        !(e instanceof RewardTokensSlipTypeError) &&
                        !(e instanceof RewardTokensMinLegsError) &&
                        !(e instanceof UnderMinimumStakePreCheckError),
                );

                if (betslipErrors.length > 1) {
                    betslipErrors = betslipErrors.filter((e) => !(e instanceof StakeNotDividablePreCheckError));
                }

                if (
                    !this.pick ||
                    (betslipErrors.some((e) => e instanceof MinSelectionsBetbuilderError) &&
                        this.quickBetService.hasMinBetBuilderSelections(this.pick))
                ) {
                    betslipErrors = betslipErrors.filter((e) => !(e instanceof MinSelectionsBetbuilderError));
                }

                if (betslipErrors.length === 0) {
                    return of([]);
                }

                return forkJoin(
                    betslipErrors.slice(0, 2).map((error) =>
                        this.errorMessageFormatter.getErrorMessage(error).pipe(
                            map((errorMessage) => {
                                if (isExtendedMessage(errorMessage)) {
                                    return {
                                        error,
                                        message: errorMessage.message,
                                    };
                                }

                                if (isSimpleMessage(errorMessage)) {
                                    return {
                                        error,
                                        message: errorMessage,
                                    };
                                }

                                return null;
                            }),
                        ),
                    ),
                );
            }),
        );
    }
}
